import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import LoadingSpinner from './components/LoadingSpinner';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import './App.css';

export default function TeacherConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    hearAbout: [], // Multiple choice
    whyJoin: '', // Short answer
    promotionalContent: '', // Yes/No
    motivation: '', // Short answer
    experience: '', // Teaching experience
    cvFile: null, // CV file
    subjects: [], // Subjects they can teach
    gradeLevels: [] // Grade levels they can teach
  });
  const [cvUploading, setCvUploading] = useState(false);

  // Subject and Grade Level options
  const subjectOptions = [
    'Math', 'Science', 'Biology', 'Chemistry', 'Physics', 'English', 'Arabic', 'French', 'Spanish', 'German',
    'History', 'Geography', 'Civics', 'Social Studies', 'Islamic Studies', 'Religious Studies',
    'Computer Science', 'ICT', 'Physical Education', 'Art', 'Music', 'Drama', 'Economics', 'Business Studies',
    'Accounting', 'Environmental Science', 'Design & Technology', 'Psychology', 'Philosophy', 'Sociology',
    'Law', 'Medicine', 'Nursing', 'Pharmacy', 'Engineering', 'Architecture', 'Marketing', 'Finance',
    'Calculus', 'Algebra', 'Statistics', 'Trigonometry', 'Literature', 'Writing', 'Grammar', 'Linguistics',
    'Biology (Advanced)', 'Computer Engineering', 'Software Engineering', 'Electrical Engineering',
    'Mechanical Engineering', 'Civil Engineering', 'Data Science', 'Artificial Intelligence', 'Robotics',
    'Other'
  ];

  const gradeLevelOptions = [
    ...Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`),
    'Foundation', 'Diploma', 'Bachelor', 'Master', 'PhD'
  ];

  // Multiple choice options for "How did you hear about Taealam?"
  const hearAboutOptions = [
    'Social Media (Facebook, Instagram, Twitter)',
    'Google Search',
    'Friend or Colleague',
    'Advertisement',
    'Educational Website',
    'Other'
  ];

  useEffect(() => {
    // Check if user is logged in and needs to complete confirmation
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // User not logged in - redirect to sign in
        setError('Please sign in first to complete your registration.');
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
        setLoading(false);
        return;
      }

      // User is logged in - check if they've completed confirmation
      setUserEmail(user.email || '');
      
      const { data: teacherData, error: teacherError } = await supabase
        .from('Teachers')
        .select('confirmation_completed, profile_photo, bio, subjects, grade_levels, languages, hourly_rate')
        .eq('email', user.email)
        .single();
      
      if (teacherError) {
        console.error('Error fetching teacher data:', teacherError);
        setError('Unable to verify your account. Please try again.');
        setLoading(false);
        return;
      }
      
      if (teacherData?.confirmation_completed) {
        // Confirmation completed - check if profile is complete
        const isProfileComplete = !!(
          teacherData?.profile_photo &&
          teacherData?.bio &&
          teacherData?.bio.trim().length > 0 &&
          teacherData?.subjects &&
          Array.isArray(teacherData.subjects) &&
          teacherData.subjects.length > 0 &&
          teacherData?.grade_levels &&
          Array.isArray(teacherData.grade_levels) &&
          teacherData.grade_levels.length > 0 &&
          teacherData?.languages &&
          Array.isArray(teacherData.languages) &&
          teacherData.languages.length > 0 &&
          teacherData?.hourly_rate &&
          teacherData.hourly_rate > 0
        );
        
        if (isProfileComplete) {
          // Both confirmation and profile completed - redirect to dashboard
          navigate('/dashboard/teacher/dashboard');
        } else {
          // Confirmation completed but profile incomplete - redirect to profile page
          navigate('/dashboard/teacher/profile');
        }
        return;
      }
      
      // User needs to complete the form
      setLoading(false);
    } catch (err) {
      console.error('Error checking user:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleHearAboutChange = (option) => {
    setFormData(prev => {
      const current = prev.hearAbout || [];
      if (current.includes(option)) {
        return { ...prev, hearAbout: current.filter(item => item !== option) };
      } else {
        return { ...prev, hearAbout: [...current, option] };
      }
    });
  };

  const handleSubjectChange = (subject) => {
    setFormData(prev => {
      const current = prev.subjects || [];
      if (current.includes(subject)) {
        return { ...prev, subjects: current.filter(item => item !== subject) };
      } else {
        return { ...prev, subjects: [...current, subject] };
      }
    });
  };

  const handleGradeLevelChange = (grade) => {
    setFormData(prev => {
      const current = prev.gradeLevels || [];
      if (current.includes(grade)) {
        return { ...prev, gradeLevels: current.filter(item => item !== grade) };
      } else {
        return { ...prev, gradeLevels: [...current, grade] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.hearAbout.length === 0) {
      setError('Please select at least one option for "How did you hear about Taealam?"');
      return;
    }

    if (!formData.whyJoin.trim()) {
      setError('Please answer "Why did you choose to join Taealam as a teacher?"');
      return;
    }

    if (!formData.promotionalContent) {
      setError('Please answer "Would you be interested in participating in promotional content?"');
      return;
    }

    if (!formData.motivation.trim()) {
      setError('Please answer "What motivates you to continue teaching?"');
      return;
    }

    if (!formData.experience.trim()) {
      setError('Please describe your teaching experience');
      return;
    }

    if (formData.subjects.length === 0) {
      setError('Please select at least one subject you can teach');
      return;
    }

    if (formData.gradeLevels.length === 0) {
      setError('Please select at least one grade level you can teach');
      return;
    }

    setSubmitting(true);

    try {
      let cvUrl = null;

      // Upload CV if provided
      if (formData.cvFile) {
        setCvUploading(true);
        try {
          const fileExt = formData.cvFile.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `teacher-cvs/${fileName}`;

          // Upload with upsert: true to overwrite if exists
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('teacher-documents')
            .upload(filePath, formData.cvFile, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('CV upload error:', uploadError);

            // More descriptive error messages
            if (uploadError.message.includes('not found') || uploadError.message.includes('bucket')) {
              throw new Error('Storage bucket not configured. Please contact support.');
            } else if (uploadError.message.includes('policy')) {
              throw new Error('Permission denied. Please ensure you are logged in.');
            } else if (uploadError.message.includes('size')) {
              throw new Error('File size too large. Maximum size is 10MB.');
            } else {
              throw new Error(`Upload failed: ${uploadError.message}`);
            }
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('teacher-documents')
            .getPublicUrl(filePath);

          cvUrl = urlData.publicUrl;
          console.log('CV uploaded successfully:', cvUrl);
        } catch (cvError) {
          console.error('CV upload exception:', cvError);
          setCvUploading(false);
          setSubmitting(false);
          setError(cvError.message || 'Failed to upload CV. Please try again.');
          return;
        } finally {
          setCvUploading(false);
        }
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('Please sign in to complete your registration.');
      }

      console.log('Submitting form for user:', user.id);
      console.log('Form data:', formData);

      // Save answers to teacher profile
      // NOTE: confirmation_completed stays FALSE until admin manually approves
      const updateData = {
        hear_about_taealam: formData.hearAbout,
        why_join_taealam: formData.whyJoin.trim(),
        promotional_content_interest: formData.promotionalContent === 'yes',
        teaching_motivation: formData.motivation.trim(),
        teaching_experience: formData.experience.trim(),
        subjects: formData.subjects,
        grade_levels: formData.gradeLevels,
        form_submitted_at: new Date().toISOString()
        // confirmation_completed remains false - admin will approve manually
      };

      // Add CV URL if uploaded
      if (cvUrl) {
        updateData.cv_url = cvUrl;
      }

      console.log('Update data:', updateData);

      // Update by email (since id is BIGINT and user.id is UUID)
      const { data: updatedData, error: updateError } = await supabase
        .from('Teachers')
        .update(updateData)
        .eq('email', user.email)
        .select();

      if (updateError) {
        console.error('Update error details:', updateError);
        console.error('Error code:', updateError.code);
        console.error('Error message:', updateError.message);
        console.error('Error details:', updateError.details);
        console.error('Error hint:', updateError.hint);
        
        // More specific error message
        if (updateError.message?.includes('permission') || updateError.message?.includes('policy') || updateError.message?.includes('RLS')) {
          throw new Error('Permission denied. Please make sure you have the correct RLS policies set up.');
        } else {
          throw new Error(`Failed to save: ${updateError.message || 'Please try again.'}`);
        }
      }

      console.log('Update successful:', updatedData);

      // Send admin notification email
      try {
        // Get teacher's full profile data to include in notification
        const { data: teacherProfile, error: profileError } = await supabase
          .from('Teachers')
          .select('full_name, email, phone, subjects, grade_levels')
          .eq('email', user.email)
          .single();

        if (!profileError && teacherProfile) {
          // Send notification to admin
          const backendUrl = getApiBaseUrl();
          await fetch(`${backendUrl}/api/notify-admin-new-teacher`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              teacherName: teacherProfile.full_name,
              teacherEmail: teacherProfile.email,
              teacherPhone: teacherProfile.phone,
              subjects: Array.isArray(teacherProfile.subjects) ? teacherProfile.subjects.join(', ') : 'Not specified',
              gradeLevel: Array.isArray(teacherProfile.grade_levels) ? teacherProfile.grade_levels.join(', ') : 'Not specified'
            })
          });
          console.log('Admin notification sent successfully');
        }
      } catch (notificationError) {
        console.error('Error sending admin notification (non-critical):', notificationError);
        // Don't fail the form submission if email notification fails
      }

      // Mark as success
      setSuccess(true);
      setSubmitting(false);

      // Don't redirect - teacher needs to wait for admin approval
      // They'll receive an email notification when approved

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'An error occurred. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF8EE',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 40,
          boxShadow: '0 8px 32px rgba(15, 37, 69, 0.1)',
          textAlign: 'center',
          maxWidth: 400,
          border: '1px solid rgba(15, 37, 69, 0.1)'
        }}>
          <LoadingSpinner size={70} />
          <div style={{ fontSize: 16, color: '#0F2545', fontWeight: 500, marginTop: 20 }}>Verifying your confirmation link...</div>
        </div>
      </div>
    );
  }

  // Show error state (e.g., user not logged in)
  if (error && error.includes('sign in')) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF8EE',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif",
        padding: 20
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 24,
          padding: 50,
          boxShadow: '0 8px 32px rgba(15, 37, 69, 0.1)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid rgba(15, 37, 69, 0.1)'
        }}>
          <FaExclamationCircle size={60} color="#FF7426" style={{ marginBottom: 20 }} />
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#0F2545',
            marginBottom: 12
          }}>
            Sign In Required
          </h2>
          <p style={{
            fontSize: 16,
            color: '#0F2545',
            marginBottom: 32,
            lineHeight: 1.6,
            opacity: 0.8
          }}>
            {error}
          </p>

          <button
            onClick={() => navigate('/signin')}
            style={{
              width: '100%',
              padding: 14,
              background: '#0F2545',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FF7426';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0F2545';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF8EE',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif",
        padding: 20
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 50,
          boxShadow: '0 8px 32px rgba(15, 37, 69, 0.1)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid rgba(15, 37, 69, 0.1)'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#FF7426',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <FaCheckCircle size={42} color="#fff" />
          </div>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#0F2545',
            marginBottom: 12
          }}>
            Account Confirmed! 🎉
          </h2>
          <p style={{
            fontSize: 16,
            color: '#0F2545',
            marginBottom: 24,
            lineHeight: 1.6,
            opacity: 0.8
          }}>
            Thank you for completing your registration form!
            <br /><br />
            <strong>What happens next?</strong>
            <br /><br />
            Our admin team will review your application. You'll receive an email notification once your account is approved. This typically takes 1-2 business days.
            <br /><br />
            Please wait for approval before signing in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FDF8EE',
      padding: '40px 20px',
      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
    }}>
      <div style={{
        maxWidth: 700,
        margin: '0 auto',
        background: '#fff',
        borderRadius: 24,
        boxShadow: '0 8px 32px rgba(15, 37, 69, 0.1)',
        overflow: 'hidden',
        border: '1px solid rgba(15, 37, 69, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          background: '#0F2545',
          padding: '40px 40px 30px',
          textAlign: 'center',
          position: 'relative'
        }}>
          {/* Decorative orange accent */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: '#FF7426'
          }} />
          <img
            src={`${window.location.origin}/logo.png`}
            alt="Taealam Logo"
            style={{
              width: 80,
              height: 80,
              marginBottom: 16,
              background: '#fff',
              borderRadius: '50%',
              padding: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}
            onError={(e) => {
              // Fallback to hosted logo if local fails
              if (!e.target.src.includes('postimg.cc')) {
                e.target.src = 'https://i.postimg.cc/zXTmvr99/logo.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
          <h1 style={{
            fontSize: 28,
            fontWeight: 800,
            color: '#fff',
            margin: 0,
            marginBottom: 8
          }}>
            Welcome to Taealam! 👋
          </h1>
          <p style={{
            fontSize: 16,
            color: 'rgba(255,255,255,0.9)',
            margin: 0
          }}>
            Please answer a few questions to complete your registration
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: 40 }}>
          {error && (
            <div style={{
              background: '#FFF4E6',
              border: '2px solid #FF7426',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              color: '#0F2545'
            }}>
              <FaExclamationCircle size={20} color="#FF7426" />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Question 1: How did you hear about Taealam? */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              1. How did you hear about Taealam? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <p style={{
              fontSize: 14,
              color: '#0F2545',
              marginBottom: 16,
              marginTop: 4,
              opacity: 0.7
            }}>
              Select all that apply
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}>
              {hearAboutOptions.map((option, index) => (
                <label
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 14,
                    border: formData.hearAbout.includes(option)
                      ? '2px solid #FF7426'
                      : '2px solid rgba(15, 37, 69, 0.2)',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: formData.hearAbout.includes(option) ? '#FFF4E6' : '#fff',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.hearAbout.includes(option)) {
                      e.currentTarget.style.borderColor = '#FF7426';
                      e.currentTarget.style.background = '#FDF8EE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.hearAbout.includes(option)) {
                      e.currentTarget.style.borderColor = 'rgba(15, 37, 69, 0.2)';
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.hearAbout.includes(option)}
                    onChange={() => handleHearAboutChange(option)}
                    style={{
                      width: 20,
                      height: 20,
                      marginRight: 12,
                      cursor: 'pointer',
                      accentColor: '#FF7426'
                    }}
                  />
                  <span style={{
                    fontSize: 15,
                    color: '#0F2545',
                    fontWeight: 500
                  }}>
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Question 2: Why join Taealam? */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              2. Why did you choose to join Taealam as a teacher? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <textarea
              value={formData.whyJoin}
              onChange={(e) => setFormData(prev => ({ ...prev, whyJoin: e.target.value }))}
              placeholder="Please share your reasons for joining our platform..."
              rows={4}
              style={{
                width: '100%',
                padding: 14,
                border: '2px solid rgba(15, 37, 69, 0.2)',
                borderRadius: 10,
                fontSize: 15,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                color: '#0F2545'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF7426'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(15, 37, 69, 0.2)'}
              maxLength={500}
            />
            <div style={{
              fontSize: 12,
              color: '#0F2545',
              marginTop: 6,
              textAlign: 'right',
              opacity: 0.6
            }}>
              {formData.whyJoin.length} / 500 characters
            </div>
          </div>

          {/* Question 3: Promotional Content */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              3. Would you be interested in participating in promotional content such as videos, photos, interviews, or teacher spotlights on our social media? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <div style={{
              display: 'flex',
              gap: 12,
              marginTop: 12
            }}>
              {['yes', 'no'].map((option) => (
                <label
                  key={option}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 16,
                    border: formData.promotionalContent === option
                      ? '2px solid #FF7426'
                      : '2px solid rgba(15, 37, 69, 0.2)',
                    borderRadius: 10,
                    cursor: 'pointer',
                    background: formData.promotionalContent === option ? '#FFF4E6' : '#fff',
                    transition: 'all 0.2s',
                    fontWeight: 600,
                    fontSize: 15,
                    color: formData.promotionalContent === option ? '#FF7426' : '#0F2545',
                    textTransform: 'capitalize',
                    opacity: formData.promotionalContent === option ? 1 : 0.8
                  }}
                  onMouseEnter={(e) => {
                    if (formData.promotionalContent !== option) {
                      e.currentTarget.style.borderColor = '#FF7426';
                      e.currentTarget.style.background = '#FDF8EE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.promotionalContent !== option) {
                      e.currentTarget.style.borderColor = 'rgba(15, 37, 69, 0.2)';
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="promotionalContent"
                    value={option}
                    checked={formData.promotionalContent === option}
                    onChange={(e) => setFormData(prev => ({ ...prev, promotionalContent: e.target.value }))}
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 8,
                      cursor: 'pointer',
                      accentColor: '#FF7426'
                    }}
                  />
                  {option === 'yes' ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
          </div>

          {/* Question 4: Motivation */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              4. What motivates you to continue teaching and offering sessions on our platform? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <textarea
              value={formData.motivation}
              onChange={(e) => setFormData(prev => ({ ...prev, motivation: e.target.value }))}
              placeholder="Share what drives you to teach and help students..."
              rows={4}
              style={{
                width: '100%',
                padding: 14,
                border: '2px solid rgba(15, 37, 69, 0.2)',
                borderRadius: 10,
                fontSize: 15,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                color: '#0F2545'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF7426'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(15, 37, 69, 0.2)'}
              maxLength={500}
            />
            <div style={{
              fontSize: 12,
              color: '#0F2545',
              marginTop: 6,
              textAlign: 'right',
              opacity: 0.6
            }}>
              {formData.motivation.length} / 500 characters
            </div>
          </div>

          {/* Question 5: Teaching Experience */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              5. Please describe your teaching experience <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <p style={{
              fontSize: 14,
              color: '#0F2545',
              marginBottom: 16,
              marginTop: 4,
              opacity: 0.7
            }}>
              Include years of experience, subjects taught, educational background, and any relevant qualifications
            </p>
            <textarea
              value={formData.experience}
              onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
              placeholder="Example: 5 years teaching Mathematics at secondary level, Bachelor's degree in Mathematics Education, experienced with online teaching platforms..."
              rows={5}
              style={{
                width: '100%',
                padding: 14,
                border: '2px solid rgba(15, 37, 69, 0.2)',
                borderRadius: 10,
                fontSize: 15,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                color: '#0F2545'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF7426'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(15, 37, 69, 0.2)'}
              maxLength={1000}
            />
            <div style={{
              fontSize: 12,
              color: '#0F2545',
              marginTop: 6,
              textAlign: 'right',
              opacity: 0.6
            }}>
              {formData.experience.length} / 1000 characters
            </div>
          </div>

          {/* Question 6: Subjects You Can Teach */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              6. Which subjects can you teach? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <p style={{
              fontSize: 14,
              color: '#0F2545',
              marginBottom: 16,
              marginTop: 4,
              opacity: 0.7
            }}>
              Select all that apply
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 10,
              maxHeight: 300,
              overflowY: 'auto',
              padding: 16,
              border: '2px solid rgba(15, 37, 69, 0.1)',
              borderRadius: 10,
              background: '#fff'
            }}>
              {subjectOptions.map((subject, index) => (
                <label
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 12,
                    border: formData.subjects.includes(subject)
                      ? '2px solid #FF7426'
                      : '2px solid rgba(15, 37, 69, 0.2)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: formData.subjects.includes(subject) ? '#FFF4E6' : '#fff',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.subjects.includes(subject)) {
                      e.currentTarget.style.borderColor = '#FF7426';
                      e.currentTarget.style.background = '#FDF8EE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.subjects.includes(subject)) {
                      e.currentTarget.style.borderColor = 'rgba(15, 37, 69, 0.2)';
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.subjects.includes(subject)}
                    onChange={() => handleSubjectChange(subject)}
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 10,
                      cursor: 'pointer',
                      accentColor: '#FF7426'
                    }}
                  />
                  <span style={{
                    fontSize: 14,
                    color: '#0F2545',
                    fontWeight: 500
                  }}>
                    {subject}
                  </span>
                </label>
              ))}
            </div>
            <div style={{
              fontSize: 13,
              color: '#0F2545',
              marginTop: 8,
              opacity: 0.7
            }}>
              {formData.subjects.length} subject{formData.subjects.length !== 1 ? 's' : ''} selected
            </div>
          </div>

          {/* Question 7: Grade Levels You Can Teach */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              7. Which grade levels can you teach? <span style={{ color: '#FF7426' }}>*</span>
            </label>
            <p style={{
              fontSize: 14,
              color: '#0F2545',
              marginBottom: 16,
              marginTop: 4,
              opacity: 0.7
            }}>
              Select all that apply
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 10,
              padding: 16,
              border: '2px solid rgba(15, 37, 69, 0.1)',
              borderRadius: 10,
              background: '#fff'
            }}>
              {gradeLevelOptions.map((grade, index) => (
                <label
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 12,
                    border: formData.gradeLevels.includes(grade)
                      ? '2px solid #FF7426'
                      : '2px solid rgba(15, 37, 69, 0.2)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: formData.gradeLevels.includes(grade) ? '#FFF4E6' : '#fff',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.gradeLevels.includes(grade)) {
                      e.currentTarget.style.borderColor = '#FF7426';
                      e.currentTarget.style.background = '#FDF8EE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.gradeLevels.includes(grade)) {
                      e.currentTarget.style.borderColor = 'rgba(15, 37, 69, 0.2)';
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.gradeLevels.includes(grade)}
                    onChange={() => handleGradeLevelChange(grade)}
                    style={{
                      width: 18,
                      height: 18,
                      marginRight: 10,
                      cursor: 'pointer',
                      accentColor: '#FF7426'
                    }}
                  />
                  <span style={{
                    fontSize: 14,
                    color: '#0F2545',
                    fontWeight: 500
                  }}>
                    {grade}
                  </span>
                </label>
              ))}
            </div>
            <div style={{
              fontSize: 13,
              color: '#0F2545',
              marginTop: 8,
              opacity: 0.7
            }}>
              {formData.gradeLevels.length} grade level{formData.gradeLevels.length !== 1 ? 's' : ''} selected
            </div>
          </div>

          {/* Question 8: CV Upload */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 16,
              fontWeight: 700,
              color: '#0F2545',
              marginBottom: 12
            }}>
              8. Upload your CV/Resume <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.7 }}>(Optional)</span>
            </label>
            <p style={{
              fontSize: 14,
              color: '#0F2545',
              marginBottom: 16,
              marginTop: 4,
              opacity: 0.7
            }}>
              Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
            </p>
            <div style={{
              border: '2px dashed rgba(15, 37, 69, 0.2)',
              borderRadius: 10,
              padding: 24,
              textAlign: 'center',
              background: '#fff',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#FF7426';
              e.currentTarget.style.background = '#FDF8EE';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(15, 37, 69, 0.2)';
              e.currentTarget.style.background = '#fff';
            }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      setError('CV file size must be less than 5MB');
                      e.target.value = '';
                      return;
                    }
                    setFormData(prev => ({ ...prev, cvFile: file }));
                    setError('');
                  }
                }}
                style={{ display: 'none' }}
                id="cv-upload"
              />
              <label htmlFor="cv-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                {formData.cvFile ? (
                  <div>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0F2545', marginBottom: 4 }}>
                      {formData.cvFile.name}
                    </div>
                    <div style={{ fontSize: 13, color: '#0F2545', opacity: 0.6 }}>
                      {(formData.cvFile.size / 1024).toFixed(1)} KB
                    </div>
                    <div style={{ fontSize: 13, color: '#FF7426', marginTop: 8, fontWeight: 600 }}>
                      Click to change file
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📎</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#0F2545', marginBottom: 4 }}>
                      Click to upload your CV
                    </div>
                    <div style={{ fontSize: 13, color: '#0F2545', opacity: 0.6 }}>
                      or drag and drop
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: 16,
              background: submitting 
                ? 'rgba(15, 37, 69, 0.3)' 
                : '#0F2545',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              boxShadow: submitting 
                ? 'none' 
                : '0 4px 12px rgba(15, 37, 69, 0.2)',
              transition: 'all 0.2s',
              opacity: submitting ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!submitting) {
                e.currentTarget.style.background = '#FF7426';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 116, 38, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting) {
                e.currentTarget.style.background = '#0F2545';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 37, 69, 0.2)';
              }
            }}
          >
            {submitting ? (
              <>
                <img
                  src={`${window.location.origin}/logo.png`}
                  alt="Loading"
                  className="spin"
                  style={{
                    width: 18,
                    height: 18,
                    display: 'inline-block'
                  }}
                  onError={(e) => {
                    // Fallback to hosted logo if local fails
                    if (!e.target.src.includes('postimg.cc')) {
                      e.target.src = 'https://i.postimg.cc/zXTmvr99/logo.png';
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                />
                Submitting...
              </>
            ) : (
              <>
                <FaCheckCircle size={18} />
                Confirm & Complete Registration
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

